import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  getCurrentUser,
  fetchAuthSession,
  signOut,
  fetchUserAttributes,
} from 'aws-amplify/auth';
import type { FetchUserAttributesOutput } from 'aws-amplify/auth';
import * as Sentry from '@sentry/react';
import { UserRole } from '../types/types';

export interface AppUser {
  userId: string;
  name?: string;
  email?: string;
  email_verified?: boolean;
  phone_number?: string;
  phone_number_verified?: boolean;
  role?: UserRole;
}

interface AuthContextType {
  user: AppUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
  checkAuthStatus: () => Promise<void>;
  login: (userData: AppUser) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const AUTH_PATHS = [
    '/',
    '/signup',
    '/signup-agent',
    '/login',
    '/otp',
    '/signup/confirmation',
    '/reset',
    '/onboarding',
  ];

  const parseVerifiedAttribute = (
    attr: string | undefined,
  ): boolean | undefined => {
    if (attr === undefined) return undefined;
    return attr.toLowerCase() === 'true';
  };

  const checkAuthStatus = useCallback(async () => {
    try {
      const cognitoUser = await getCurrentUser();
      const userId = cognitoUser.userId;
      const attributes = await fetchUserAttributes();

      const userData: AppUser = {
        userId: userId,
        name: attributes.name,
        email: attributes.email,
        email_verified: parseVerifiedAttribute(attributes.email_verified),
        phone_number: attributes.phone_number,
        phone_number_verified: parseVerifiedAttribute(
          attributes.phone_number_verified,
        ),
        role: attributes['custom:role'] as UserRole | undefined,
      };

      
      setUser(userData);
      setError(null);

      
      Sentry.setUser({
        id: userData.userId,
        email: userData.email,
        role: userData.role,
      });

      const isAuthPath = AUTH_PATHS.includes(location.pathname);
      if (isAuthPath) {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      console.warn(
        'User is not authenticated or error fetching attributes:',
        err,
      );
      setUser(null);
      setError(null);

      
      Sentry.setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, [navigate, location.pathname]);

  useEffect(() => {
    
    checkAuthStatus();
  }, [checkAuthStatus]);

  const login = useCallback((userData: AppUser) => {
    setUser(userData);
    setError(null);
    setIsLoading(false);

    
    Sentry.setUser({
      id: userData.userId,
      email: userData.email,
      role: userData.role,
    });

    
  }, []);

  const logout = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      await signOut({ global: true });
      setUser(null);

      
      Sentry.setUser(null);

      
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('Error during sign out:', err);
      setError(err instanceof Error ? err : new Error('Sign out failed'));
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const contextValue = useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      error,
      checkAuthStatus,
      login,
      logout,
    }),
    [user, isLoading, error, checkAuthStatus, login, logout],
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
